import React, { useState, useEffect } from 'react';
import { BACKEND } from '../constants';

const C = {
  navy: '#1B2B4B',
  gold: '#C8963E',
  border: '#e2e8f0',
  muted: '#94a3b8',
  green: '#16a34a',
  greenLight: '#dcfce7',
  card: '#ffffff',
};

export default function AssetUrlEditor({ item, onSaved }) {
  const isVideo = ['short_video', 'long_video', 'yt_short', 'reel'].includes(item.contentType);

  const [imageUrl, setImageUrl]       = useState(item.imageUrl || item.image_url || '');
  const [videoUrl, setVideoUrl]       = useState(item.videoUrl || item.video_url || '');
  const [pinterestUrl, setPinterestUrl] = useState(item.pinterestUrl || item.pinterest_url || '');
  const [saving, setSaving]           = useState(null); // 'image'|'video'|'pinterest'
  const [saved, setSaved]             = useState({});
  const [open, setOpen]               = useState(false);

  useEffect(() => {
    setImageUrl(item.imageUrl || item.image_url || '');
    setVideoUrl(item.videoUrl || item.video_url || '');
    setPinterestUrl(item.pinterestUrl || item.pinterest_url || '');
  }, [item.id]);

  const saveField = async (field, value) => {
    setSaving(field);
    try {
      const res = await fetch(`${BACKEND}/api/queue/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      const data = await res.json();
      if (data.success) {
        setSaved(prev => ({ ...prev, [field]: true }));
        setTimeout(() => setSaved(prev => ({ ...prev, [field]: false })), 2000);
        if (onSaved) onSaved(item.id, field, value);
      }
    } catch (e) {
      alert('Save failed: ' + e.message);
    }
    setSaving(null);
  };

  const hasUrls = imageUrl || videoUrl || pinterestUrl;

  return (
    <div style={{ marginTop: 8, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          fontSize: 11, fontWeight: 600, color: hasUrls ? C.green : C.gold,
          background: hasUrls ? C.greenLight : '#fffbeb',
          border: `1px solid ${hasUrls ? '#86efac' : '#fde68a'}`,
          borderRadius: 6, padding: '3px 9px', cursor: 'pointer',
        }}
      >
        {hasUrls ? '✅ URLs saved' : '🔗 Add URLs'} {open ? '▲' : '▼'}
      </button>

      {open && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 7 }}>

          {/* Image URL — show for non-video posts */}
          {!isVideo && (
            <UrlField
              label="🖼 Image URL"
              value={imageUrl}
              onChange={setImageUrl}
              onSave={() => saveField('imageUrl', imageUrl)}
              saving={saving === 'image'}
              saved={saved.image}
              fieldKey="image"
            />
          )}

          {/* Video URL — show for video posts */}
          {isVideo && (
            <UrlField
              label="🎥 Video URL"
              value={videoUrl}
              onChange={setVideoUrl}
              onSave={() => saveField('videoUrl', videoUrl)}
              saving={saving === 'video'}
              saved={saved.video}
              fieldKey="video"
            />
          )}

          {/* Pinterest URL — always show */}
          <UrlField
            label="📌 Pinterest URL"
            value={pinterestUrl}
            onChange={setPinterestUrl}
            onSave={() => saveField('pinterestUrl', pinterestUrl)}
            saving={saving === 'pinterest'}
            saved={saved.pinterest}
            fieldKey="pinterest"
          />

        </div>
      )}
    </div>
  );
}

function UrlField({ label, value, onChange, onSave, saving, saved, fieldKey }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 11, color: C.muted, minWidth: 100 }}>{label}</span>
      <input
        type="url"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Paste URL…"
        style={{
          flex: 1, fontSize: 11, padding: '4px 8px',
          border: `1px solid ${C.border}`, borderRadius: 6,
          outline: 'none', fontFamily: 'inherit',
        }}
        onKeyDown={e => e.key === 'Enter' && onSave()}
      />
      <button
        onClick={onSave}
        disabled={saving || !value}
        style={{
          fontSize: 11, fontWeight: 600, padding: '4px 10px',
          background: saved ? C.green : C.navy,
          color: '#fff', border: 'none', borderRadius: 6,
          cursor: saving || !value ? 'not-allowed' : 'pointer',
          opacity: !value ? 0.5 : 1, minWidth: 48,
        }}
      >
        {saving ? '…' : saved ? '✓' : '💾'}
      </button>
    </div>
  );
}
